import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import './MealPlanViewer.css';
import { parseEstDate, getDayOfWeek, getCutoffTime, sortDeliveryDates } from './services/dateUtils';

// Components
import CountdownLoader from './countdownLoader';
import MealPlanHeader from './components/Header/MealPlanHeader';
import { SuccessMessage, ErrorMessage, LockMessage } from './components/Messages/StatusMessages';
import DeliveryDateTabs from './components/DeliveryDateTabs/DeliveryDateTabs';
import ResumeDelivery from './components/SkipDelivery/ResumeDelivery';
import CompactMealCard from './components/MealCard/CompactMealCard';
import ScrollArrows from './components/MealCard/ScrollArrows';
import MealModal from './components/Modal/MealModal';
import PremiumProteinUpgradeModal from './components/Modal/PremiumProteinUpgradeModal';
import SummaryModal from './components/Modal/SummaryModal';

// Hooks
import { useScrollArrows } from './hooks/useScrollArrows';
import { useLockStatus } from './hooks/useLockStatus';
import { useIsDesktop } from './hooks/useMediaQuery';

// Order Summary Components
import { OrderSummarySidebar, FloatingOrderButton } from './components/OrderSummary';

// Services
import * as mealPlanService from './services/mealPlanService';

// Constants
import { MEAL_TYPES, MEAL_PRICES, SUBSCRIPTION_DISCOUNT } from './constants/meals';

export default function MealPlanViewer() {
    const [params] = useSearchParams();
    const token = params.get('customer');

    // State
    const [customerDetails, setCustomerDetails] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [saving, setSaving] = useState(false); // May be used for future batch save loading state
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Modal state
    const [selectedMeal, setSelectedMeal] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPriceConfirm, setShowPriceConfirm] = useState(null);
    const [showAddedBanner, setShowAddedBanner] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);

    // Price change state
    const [billingChanges, setBillingChanges] = useState(null);

    // Delivery date state
    const [deliveryDates, setDeliveryDates] = useState([]);
    const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
    const [ordersByDeliveryDate, setOrdersByDeliveryDate] = useState({});
    const [deliveryStatusState, setDeliveryStatusState] = useState({});

    // Pending changes state - tracks unsaved changes per delivery date
    const [pendingChanges, setPendingChanges] = useState({});
    // Structure:
    // {
    //   "2025-01-15": {
    //     isDirty: true,
    //     changes: [{ type: 'ADD_DISH', ... }, { type: 'REPLACE_PROTEIN', ... }, ...],
    //     originalOrders: [...] // snapshot for discard/rollback
    //   }
    // }

    // Refs
    const modalRef = useRef(null);
    const containerRef = useRef(null);

    // Custom hooks
    const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll, initializeScrollPositions } = useScrollArrows();
    const isLocked = useLockStatus(selectedDeliveryDate);
    const isDesktop = useIsDesktop();

    // Effects
    useEffect(() => {
        if (token) {
            fetchMealPlan();

        } else {
            setError('No customer token provided');
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Handle Stripe redirect - process stored pending changes after successful payment
    // This is a ref to track if we've already processed the payment redirect
    const paymentProcessedRef = useRef(false);
    useEffect(() => {
        const processStoredPendingChanges = async () => {
            // Check URL params for Stripe redirect status
            const paymentStatus = params.get('payment');
            const deliveryDate = params.get('date');

            // Only process once per redirect
            if (paymentStatus === 'success' && deliveryDate && token && !paymentProcessedRef.current) {
                paymentProcessedRef.current = true;

                const storageKey = `pendingChanges-${deliveryDate}`;
                const storedData = sessionStorage.getItem(storageKey);

                if (storedData) {
                    try {
                        const { changes } = JSON.parse(storedData);

                        if (changes?.length > 0) {
                            // Call batch save to persist changes to Airtable
                            const response = await mealPlanService.batchSave(token, deliveryDate, changes, billingChanges);

                            if (response.data.success) {
                                setSuccessMessage(`Payment successful! Your changes for ${deliveryDate} have been saved.`);
                            } else {
                                setError('Payment was successful, but there was an issue saving your changes. Please contact support.');
                            }
                        } else {
                            setSuccessMessage(`Payment successful for ${deliveryDate}!`);
                        }

                        // Clean up sessionStorage
                        sessionStorage.removeItem(storageKey);

                    } catch (err) {
                        setError('Payment was successful, but there was an issue saving your changes. Please contact support.');
                        sessionStorage.removeItem(storageKey);
                    }
                } else {
                    // No stored changes, just show success message
                    setSuccessMessage(`Payment successful for ${deliveryDate}!`);
                }

                // Refresh data to get updated state from Airtable
                await fetchMealPlan();

                // Clean up URL params after everything is done
                window.history.replaceState({}, '', `/meal-plan?customer=${token}`);

                setTimeout(() => setSuccessMessage(''), 8000);
            }
        };

        processStoredPendingChanges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, params]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            initializeScrollPositions(MEAL_TYPES);
        }, 100);
        return () => clearTimeout(timeout);
    }, [orders, initializeScrollPositions]);

    useEffect(() => {
        if (customerDetails?.customer?.name) {
            document.title = `Square Fare for ${customerDetails.customer.name}`;
        } else if (!loading) {
            document.title = 'Square Fare Meal Plan';
        }
    }, [customerDetails?.customer?.name, loading]);

    // Update delivery statuses when customerDetails or deliveryDates change
    useEffect(() => {
        updateDeliveryStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerDetails?.subscriptions]);

    // Calculate price deltas across all delivery dates
    const calculateBillingChanges = useCallback(() => {
        if (!customerDetails?.subscriptions || !deliveryDates.length) return null;

        let totalCharges = 0;
        let totalRefunds = 0;
        const datesWithChanges = [];
        const dates = {};

        deliveryDates.forEach(date => {
            //check if date is passed deadlines
            const lockDate = getCutoffTime(date);
            if (lockDate && new Date(date) <= new Date(lockDate.getTime() + 1000 * 60 * 60)) {
                return;
            }

            // Check if this date is skipped - local state takes priority over subscription data
            const localSkipStatus = deliveryStatusState[date]?.isSkipped;
            const dateDayOfWeek = getDayOfWeek(date).toLowerCase(); // Use EST-aware day of week

            // Determine if date is skipped: local state takes priority, otherwise check subscription
            const matchingSubscriptions = customerDetails.subscriptions.filter(sub =>
                sub.deliveryDay?.toLowerCase() === dateDayOfWeek
            );

            // If local state explicitly says not skipped (resumed), don't treat as skipped
            // If local state says skipped, treat as skipped
            // If local state is undefined, fall back to subscription isSkipped status
            const isDateSkipped = localSkipStatus !== undefined
                ? localSkipStatus
                : matchingSubscriptions.some(sub => sub.isSkipped);

            // Check if this is a skipped delivery
            if (isDateSkipped) {


                // Build meals object for skipped delivery
                const meals = {};
                matchingSubscriptions.forEach(sub => {
                    const mealType = sub.mealType;
                    if (!meals[mealType]) {
                        meals[mealType] = {
                            subscribed: 0,
                            ordered: 0,
                            delta: 0,
                            amount: 0,
                            premiumProteins: { count: 0, charge: 0 }
                        };
                    }
                    let mealsIncluded = sub.mealsIncluded || 0;
                    if (mealType === 'Snack') {
                        mealsIncluded = mealsIncluded / customerDetails.customer.snacksPerDay;
                    }
                    meals[mealType].subscribed += mealsIncluded;
                    meals[mealType].delta -= mealsIncluded;
                    meals[mealType].amount += mealsIncluded * MEAL_PRICES[mealType] * SUBSCRIPTION_DISCOUNT;
                });

                const refundAmount =
                    Object.values(meals)
                        .reduce((sum, meal) => sum + (meal.amount ?? 0), 0).toFixed(2);

                dates[date] = {
                    totalSubscribed: matchingSubscriptions.reduce((acc, sub) => acc + sub.mealsIncluded, 0),
                    totalOrdered: 0,
                    hasChanges: true,
                    isSkipped: true,
                    charges: 0,
                    refunds: refundAmount,
                    net: -refundAmount,
                    meals
                };

                totalRefunds += refundAmount;
                datesWithChanges.push(date);
                return;
            }

            // Calculate subscriptions by meal type for this date
            // Use matchingSubscriptions which already filtered by day of week
            const subscriptionsByMealType = {};
            matchingSubscriptions.forEach(sub => {
                const mealType = sub.mealType;
                let mealsIncluded = sub.mealsIncluded;
                if (mealType === 'Snack') {
                    mealsIncluded = mealsIncluded / customerDetails.customer.snacksPerDay;
                } //if snack, divide by snacks per day = number of days subscribed
                subscriptionsByMealType[mealType] = (subscriptionsByMealType[mealType] || 0) + (mealsIncluded || 0);
            });

            // Calculate orders by meal type for this date, tracking premium proteins per meal
            const ordersForDate = ordersByDeliveryDate[date] || [];
            const orderedByMealType = {};
            const premiumProteinsByMealType = {};

            // Initialize these before the loop since Add-On handling needs them
            let dateCharges = 0;
            let dateRefunds = 0;
            const meals = {};

            ordersForDate.forEach(order => {
                const mealType = order.meal || 'Unknown';
                let quantity = order.quantity;
                if (mealType === 'Snack') {
                    quantity = quantity / customerDetails.customer.snacksPerDay;
                }
                orderedByMealType[mealType] = (orderedByMealType[mealType] || 0) + quantity;

                // Check for premium protein surcharges - only count if order has quantity > 0
                if (order.isOrdered) {
                    const isPremiumPaid = customerDetails.subscriptions?.some(
                        subscription => subscription?.meal?.toLowerCase().includes(mealType.toLowerCase()) &&
                            subscription?.tags?.includes('Premium Meat Only Paid')
                    );
                    const proteinSurcharge = order.proteinOptions?.find(opt => opt.isActive)?.price || 0;
                    if (proteinSurcharge > 0 && !isPremiumPaid) {
                        if (!premiumProteinsByMealType[mealType]) {
                            premiumProteinsByMealType[mealType] = { count: 0, charge: 0 };
                        }
                        premiumProteinsByMealType[mealType].charge += proteinSurcharge;
                        premiumProteinsByMealType[mealType].count += 1;
                    }
                }

                // check if add-on is ordered
                if (order.meal === 'Add On' && order.quantity > 0) {
                    const addOnPrice = order.price || 0;
                    const quantity = order.quantity;
                    // Accumulate add-on orders (in case there are multiple add-ons)
                    if (!meals['Add On']) {
                        meals['Add On'] = {
                            subscribed: 0,
                            ordered: 0,
                            delta: 0,
                            amount: 0,
                            price: 0
                        };
                    }
                    meals['Add On'].ordered += quantity;
                    meals['Add On'].delta += quantity;
                    meals['Add On'].amount += addOnPrice * quantity;
                    meals['Add On'].price += addOnPrice * quantity;
                    dateCharges += addOnPrice * quantity;
                }
            });

            // Calculate totals
            const totalSubscribed = Object.values(subscriptionsByMealType).reduce((sum, count) => sum + count, 0);
            const totalOrdered = Object.values(orderedByMealType).reduce((sum, count) => sum + count, 0);

            // Include all meal types except for add-on (handled separately above)
            MEAL_TYPES.forEach(mealType => {
                // Skip Add On - it's handled separately with per-item pricing
                if (mealType === 'Add On') return;

                const subscribed = subscriptionsByMealType[mealType] || 0;
                const ordered = Math.floor(orderedByMealType[mealType] || 0);
                const delta = ordered - subscribed;
                const mealPrice = MEAL_PRICES[mealType];
                const amount = delta > 0 ? delta * mealPrice : delta * mealPrice * SUBSCRIPTION_DISCOUNT; // Can be positive or negative. apply discount if delta is negative.

                meals[mealType] = {
                    subscribed,
                    ordered,
                    delta,
                    amount,
                    premiumProteins: premiumProteinsByMealType[mealType] || { count: 0, charge: 0 }
                };

                // Track charges and refunds
                if (delta > 0) {
                    dateCharges += amount;
                } else if (delta < 0) {
                    dateRefunds += Math.abs(amount);
                }

                // Add premium protein charges to date charges
                if (meals[mealType].premiumProteins.charge > 0) {
                    dateCharges += meals[mealType].premiumProteins.charge;
                }
            });

            // Create date entry
            const hasChanges = dateCharges > 0 || dateRefunds > 0;

            dates[date] = {
                totalSubscribed,
                totalOrdered,
                hasChanges,
                isSkipped: false,
                charges: dateCharges,
                refunds: dateRefunds,
                net: dateCharges - dateRefunds,
                meals
            };

            // Update global totals and track dates with changes
            if (hasChanges) {
                totalCharges += dateCharges;
                totalRefunds += dateRefunds;
                datesWithChanges.push(date);
            }
        });

        return {
            summary: {
                totalCharges,
                totalRefunds,
                netChange: totalCharges - totalRefunds,
                datesWithChanges,
                customerName: customerDetails?.customer?.name
            },
            dates
        };
    }, [customerDetails, deliveryDates, ordersByDeliveryDate, deliveryStatusState]);

    // API functions
    const fetchMealPlan = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const { customerDetails, MealsByDate } = await mealPlanService.fetchMealPlan(token);
            const sortedDates = sortDeliveryDates(Object.keys(MealsByDate));

            setCustomerDetails(customerDetails);
            setDeliveryDates(sortedDates);
            setOrdersByDeliveryDate(MealsByDate);
            setSelectedDeliveryDate(sortedDates[0]);
            setOrders(MealsByDate[sortedDates[0]] || []);
            //console.log('customerDetails', customerDetails);
            //console.log('MealsByDate', MealsByDate);

        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to load meal plan. Please try again later.';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Handle Stripe payment cancelled redirect
    useEffect(() => {
        const paymentStatus = params.get('payment');

        if (paymentStatus === 'cancelled') {
            // Don't show error message, just reload the page to refresh data
            window.location.href = `/meal-plan?customer=${token}`;
        }
    }, [params, token]);

    // Detect price changes whenever orders or delivery status changes
    useEffect(() => {
        if (customerDetails && deliveryDates.length > 0) {
            const changes = calculateBillingChanges();
            //console.log('changes', changes);
            setBillingChanges(changes);
        }
    }, [ordersByDeliveryDate, customerDetails, deliveryDates, deliveryStatusState, calculateBillingChanges]);

    // Helper functions
    const getSubscriptionCount = (mealType = null) => {
        if (!customerDetails?.subscriptions) return 0;
        const subscription = customerDetails.subscriptions.find(sub =>
            (mealType ? sub.meal?.toLowerCase().includes(mealType.toLowerCase()) : true) &&
            sub.deliveryDay.toLowerCase() === getDayOfWeek(selectedDeliveryDate).toLowerCase()
        );
        return subscription?.mealsIncluded || 0;
    };

    // Update delivery status for all dates based on subscription data
    const updateDeliveryStatuses = () => {
        if (!customerDetails?.subscriptions || !deliveryDates || deliveryDates.length === 0) return;

        const newStatuses = {};

        deliveryDates.forEach(date => {
            // Parse date to get day of week
            const parsedDate = parseEstDate(date);
            const dayOfWeek = parsedDate.format("dddd");

            // Find subscriptions for this day of week
            const daySubscription = customerDetails.subscriptions.find(sub =>
                sub.deliveryDay?.toLowerCase() === dayOfWeek.toLowerCase()
            );
            if (!daySubscription) {
                newStatuses[date] = { isSkipped: false, hasSubscription: false };
            } else {
                newStatuses[date] = {
                    isSkipped: daySubscription.isSkipped,
                    hasSubscription: true,
                    nextDeliveryDate: daySubscription.nextDeliveryDate
                };
            }
        });

        setDeliveryStatusState(newStatuses);
    };

    const getOrderedMealCount = (mealType, deliveryDate = null) => {
        const targetDate = deliveryDate || selectedDeliveryDate;
        const orderedCount = orders
            .filter(order =>
                order.meal === mealType &&
                order.deliveryDate === targetDate &&
                order.quantity > 0
            )
            .reduce((total, order) => total + order.quantity, 0);
        return orderedCount;
    };

    const getSubscriptionText = (mealType, subscribed, ordered) => {
        const snacksPerDay = customerDetails.customer.snacksPerDay;
        let finalText = '';
        if (mealType === 'Snack') {
            finalText = subscribed === 1 ? `${subscribed / snacksPerDay} day subscribed` : `${subscribed / snacksPerDay} days subscribed` + ', ' + (snacksPerDay > 1 ? `${snacksPerDay} snacks per day` : '1 snack per day');
        } else if (mealType.toLowerCase() === 'add on') {
            finalText = `${ordered} selected`;
        } else {
            finalText = `${subscribed} meal${subscribed !== 1 ? 's' : ''} subscribed, ${ordered} selected`;
        }
        finalText += MEAL_PRICES[mealType] ? `, $${MEAL_PRICES[mealType]} per additional meal` : '';
        return finalText;
    };

    const updateOrdersState = (updatedOrders) => {
        const sorted = [
            ...updatedOrders.filter(order => order.quantity > 0),
            ...updatedOrders.filter(order => order.quantity === 0)
        ];
        setOrders(sorted);
        setOrdersByDeliveryDate(prev => ({
            ...prev,
            [selectedDeliveryDate]: sorted
        }));

        // Update modal index if modal is open
        if (selectedMeal && showModal) {
            const newIndex = sorted.findIndex(order =>
                order.recordId === selectedMeal.recordId &&
                order.dishId === selectedMeal.dishId &&
                order.meal === selectedMeal.meal
            );
            if (newIndex !== -1) {
                setSelectedMeal({ ...sorted[newIndex], actualIndex: newIndex });
            }
        }
    };

    // Helper function to optimize pending changes by filtering redundant operations
    // Groups changes by recordId and applies filtering logic similar to backend
    const optimizePendingChanges = useCallback((changes) => {
        if (!changes || changes.length === 0) return [];

        // Sort by timestamp
        const sortedChanges = [...changes].sort((a, b) => a.timestamp - b.timestamp);

        // Group changes by recordId
        const changesByRecordId = {};
        for (const change of sortedChanges) {
            const recordId = change.recordId || change.order?.recordId;
            if (!recordId) continue;

            if (!changesByRecordId[recordId]) {
                changesByRecordId[recordId] = [];
            }
            changesByRecordId[recordId].push({ ...change, _recordId: recordId });
        }

        const filteredChanges = [];
        for (const recordId in changesByRecordId) {
            const groupChanges = changesByRecordId[recordId].sort((a, b) => a.timestamp - b.timestamp);

            // Find all REMOVE_DISH changes where recordId starts with temp-
            const removeIndices = [];
            groupChanges.forEach((change, index) => {
                if (change.type === 'REMOVE_DISH' && recordId.startsWith('temp-')) {
                    removeIndices.push(index);
                }
            });

            if (removeIndices.length > 0) {

                // ADD then REMOVE for temp- recordId cancels out
                // Keep only changes that come after the last REMOVE_DISH
                const lastRemoveIndex = removeIndices[removeIndices.length - 1];
                const changesAfterLastRemove = groupChanges.slice(lastRemoveIndex + 1);
                if (changesAfterLastRemove.length > 0) {
                    filteredChanges.push(...changesAfterLastRemove);
                }


            } else {
                // Check if last change is REMOVE_DISH with non-temp recordId
                const lastChange = groupChanges[groupChanges.length - 1];
                if (lastChange.type === 'REMOVE_DISH' && !recordId.startsWith('temp-')) {
                    // Keep only the last REMOVE_DISH change (discard all modifications before deletion)
                    filteredChanges.push(lastChange);
                } else {
                    // No special REMOVE_DISH conditions, include all changes
                    filteredChanges.push(...groupChanges);
                }
            }
        }

        // Sort filtered changes by timestamp again to maintain overall order
        return filteredChanges.sort((a, b) => a.timestamp - b.timestamp);
    }, []);

    // Helper function to track pending changes for a delivery date
    // Optimizes changes by filtering redundant operations (e.g., ADD then REMOVE cancels out)
    const trackPendingChange = useCallback((deliveryDate, change) => {
        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate] || {
                isDirty: false,
                changes: [],
                monetaryChange: false
            };

            // Add the new change and optimize
            const allChanges = [...dateChanges.changes, change];
            const optimizedChanges = optimizePendingChanges(allChanges);

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: optimizedChanges.length > 0,
                    changes: optimizedChanges,
                    monetaryChange: optimizedChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, [optimizePendingChanges]);

    // Helper function to clear pending changes for a delivery date
    const clearPendingChanges = useCallback((deliveryDate) => {
        setPendingChanges(prev => {
            const { [deliveryDate]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    // Helper function to update orders with real Airtable recordIds after save
    const updateOrderRecordIds = useCallback((deliveryDate, results) => {
        if (!results || !Array.isArray(results)) return;

        // Find ADD_DISH results that have new recordIds
        const recordIdMappings = results
            .filter(r => r.type === 'ADD_DISH' && r.success && r.recordId && r.originalTempRecordId)
            .map(r => ({ oldId: r.originalTempRecordId, newId: r.recordId }));

        if (recordIdMappings.length === 0) return;

        //console.log('📝 Updating order recordIds:', recordIdMappings);

        // Update orders for the delivery date
        setOrdersByDeliveryDate(prev => {
            const ordersForDate = prev[deliveryDate] || [];
            const updatedOrders = ordersForDate.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            });
            return { ...prev, [deliveryDate]: updatedOrders };
        });

        // Also update current orders if viewing the same date
        if (selectedDeliveryDate === deliveryDate) {
            setOrders(prev => prev.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            }));
        }
    }, [selectedDeliveryDate]);

    // Helper function to mark original ingredients as active in ingredient options
    // Also filters out allergy ingredients from being active
    // Uses allergyIngredientIds from customerDetails (fetched from backend API)
    const markOriginalIngredientsActive = (order) => {
        const mealType = order.meal || 'Unknown';
        const deliveryDate = order.deliveryDate || 'Unknown';
        // Get original ingredient IDs
        const originalIngredientIds = order.originalIngredients?.map(ing => ing.id) || [];

        // Get allergy ingredient IDs from customerDetails (populated by backend API)
        const allergyIngredientIds = customerDetails?.customer?.clientAllergies[order.meal] || [];

        // Check if customer is premium and get dish name for premium protein lookup
        const isPremiumCustomer = subscriptions?.some(subscription => subscription?.meal?.toLowerCase().includes(mealType?.toLowerCase()) && subscription?.nextDeliveryDate?.toLowerCase() === deliveryDate?.toLowerCase() && (subscription?.tags?.includes('Premium Meat Only') || subscription?.tags?.includes('Premium Meat Only Paid')));

        const proteinOptions = order.proteinOptions;

        // Filter out allergy ingredients from the original ingredients
        let filteredIngredientIds = originalIngredientIds.filter(id => !allergyIngredientIds.includes(id));

        // Mark options as active if they're in the filtered original ingredients
        const markOptions = (options) => {
            if (!options) return [];
            return options.map(opt => ({
                ...opt,
                isActive: filteredIngredientIds.includes(opt.id)
            }));
        };

        // if premium customer, randomly pick a premium protein which is not an allergy
        if (isPremiumCustomer) {
            const premiumProtein = proteinOptions.filter(opt => opt.variantType?.toLowerCase() === 'premium' && !allergyIngredientIds.includes(opt.id));
            if (premiumProtein.length > 0) {
                const randomIndex = Math.floor(Math.random() * premiumProtein.length);
                const selectedPremiumId = premiumProtein[randomIndex].id;
                proteinOptions[randomIndex].isActive = true;
                filteredIngredientIds.push(selectedPremiumId);

                //mark other protein options as inactive and remove from filteredIngredientIds
                proteinOptions.forEach(opt => {
                    if (opt.id !== selectedPremiumId) {
                        opt.isActive = false;
                        filteredIngredientIds = filteredIngredientIds.filter(id => id !== opt.id);
                    }
                });
            }
        }

        // Collect active ingredients from all five option arrays
        const allOptions = [
            ...(order.proteinOptions || []),
            ...(order.veggieOptions || []),
            ...(order.starchOptions || []),
            ...(order.sauceOptions || []),
            ...(order.garnishOptions || [])
        ];
        const finalIngredients = allOptions.filter(opt => filteredIngredientIds.includes(opt.id));
        return {
            proteinOptions: markOptions(order.proteinOptions),
            veggieOptions: markOptions(order.veggieOptions),
            starchOptions: markOptions(order.starchOptions),
            sauceOptions: markOptions(order.sauceOptions),
            garnishOptions: markOptions(order.garnishOptions),
            finalIngredients
        };
    };

    // Event handlers
    const handleDeliveryDateChange = (deliveryDate) => {
        setSelectedDeliveryDate(deliveryDate);
        const ordersForDate = ordersByDeliveryDate[deliveryDate] || [];
        setOrders(ordersForDate);
    };

    const openModal = (meal, actualIndex) => {
        setSelectedMeal({ ...meal, actualIndex });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMeal(null);
        setShowAddedBanner(false);
    };

    const handleQuantityChange = (index, newQuantity) => {
        const order = orders[index];
        const oldQuantity = order.quantity;
        const finalQuantity = Math.max(0, parseInt(newQuantity) || 0);
        const scrollTop = modalRef.current?.scrollTop || 0;

        if (finalQuantity === oldQuantity) return; // No change

        let updatedOrders = [...orders];
        // Track the recordId of the order we're modifying/adding for modal lookup
        let targetRecordId = order.recordId;

        if (finalQuantity > oldQuantity) {
            // Adding a meal - local state update only
            if (order.isCore && !order.isOrdered) {
                // First time adding a core meal - mark original ingredients as active (minus allergies)
                const markedIngredients = markOriginalIngredientsActive(order);

                updatedOrders[index] = {
                    ...updatedOrders[index],
                    quantity: 1,
                    isOrdered: true,
                    hasUnsavedChange: true,
                    // Apply marked ingredients (original ingredients minus allergies)
                    proteinOptions: markedIngredients.proteinOptions,
                    veggieOptions: markedIngredients.veggieOptions,
                    starchOptions: markedIngredients.starchOptions,
                    sauceOptions: markedIngredients.sauceOptions,
                    garnishOptions: markedIngredients.garnishOptions,
                    finalIngredients: markedIngredients.finalIngredients
                };
                targetRecordId = updatedOrders[index].recordId;

                // Track the change
                trackPendingChange(selectedDeliveryDate, {
                    type: 'ADD_DISH',
                    order: updatedOrders[index],
                    timestamp: Date.now(),
                    monetaryChange: true
                });
            } else {
                // Adding another serving of an existing ordered dish
                const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const newOrder = {
                    ...order,
                    recordId: tempId,
                    quantity: 1,
                    hasUnsavedChange: true
                };
                updatedOrders.push(newOrder);
                targetRecordId = tempId;

                // Track the change
                trackPendingChange(selectedDeliveryDate, {
                    type: 'ADD_DISH',
                    order: newOrder,
                    timestamp: Date.now(),
                    monetaryChange: true
                });
            }

            // Sort: ordered items first, then unordered
            const sorted = [
                ...updatedOrders.filter(o => o.quantity > 0),
                ...updatedOrders.filter(o => o.quantity === 0)
            ];

            // Find the meal in the sorted array for modal
            const reorderedIndex = sorted.findIndex(o =>
                o.recordId === targetRecordId &&
                o.dishId === order.dishId &&
                o.meal === order.meal
            );

            // Update orders state
            flushSync(() => {
                setOrders(sorted);
                setOrdersByDeliveryDate(prev => ({
                    ...prev,
                    [selectedDeliveryDate]: sorted
                }));
            });

            // setSuccessMessage(`Added ${order.itemName}! Remember to "Save Changes" in Order Summary.`);
            // setTimeout(() => setSuccessMessage(''), 5000);

            if (reorderedIndex !== -1) {
                const mealForModal = { ...sorted[reorderedIndex], actualIndex: reorderedIndex };
                setSelectedMeal(mealForModal);
                setShowModal(true);
                setShowAddedBanner(true);
            }

        } else {
            // Removing a meal - local state update only
            const hasSameDish = updatedOrders.some((obj, i) =>
                i !== index &&
                obj.meal === order.meal &&
                obj.dishId === order.dishId &&
                obj.deliveryDate === order.deliveryDate &&
                obj.itemName === order.itemName
            );

            // Track the change before modifying state
            trackPendingChange(selectedDeliveryDate, {
                type: 'REMOVE_DISH',
                recordId: order.recordId,
                timestamp: Date.now(),
                monetaryChange: true
            });

            if (!hasSameDish) {
                // Keep the dish in list but mark as not ordered
                updatedOrders[index] = {
                    ...updatedOrders[index],
                    quantity: 0,
                    isOrdered: false,
                    hasUnsavedChange: false
                };
            } else {
                // Remove the duplicate entry
                updatedOrders = updatedOrders.filter((_, i) => i !== index);
            }

            flushSync(() => updateOrdersState(updatedOrders));

            // setSuccessMessage(`Removed ${order.itemName}. Remember to "Save Changes" in Order Summary.`);
            // setTimeout(() => setSuccessMessage(''), 5000);
        }

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleProteinSubstitution = (orderIndex, newProteinId, oldProteinId) => {
        const order = orders[orderIndex];
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);
        const upgradePrice = newProteinOption?.price || 0;
        const subscriptions = customerDetails.subscriptions;
        const isPremiumPaid = subscriptions?.some(subscription => subscription?.meal?.toLowerCase().includes(order?.meal?.toLowerCase()) && subscription?.nextDeliveryDate?.toLowerCase() === selectedDeliveryDate?.toLowerCase() && subscription?.tags?.includes('Premium Meat Only Paid'));
        if (upgradePrice > 0 && !isPremiumPaid) {
            setShowPriceConfirm({
                orderIndex,
                newProteinId,
                oldProteinId,
                proteinName: newProteinOption.variantName || newProteinOption.name,
                price: upgradePrice
            });
            return;
        }

        executeProteinSubstitution(orderIndex, newProteinId, oldProteinId, 0);
    };

    const executeProteinSubstitution = (orderIndex, newProteinId, oldProteinId, upgradePrice = 0) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);

        // Update local state - update protein options to reflect new selection
        const updatedProteinOptions = order.proteinOptions.map(opt => ({
            ...opt,
            isActive: opt.id === newProteinId
        }));

        // Update finalIngredients - replace old protein with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (oldProteinId) {
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== oldProteinId);
        }
        if (newProteinId) {
            updatedFinalIngredients.push({ id: newProteinId, name: newProteinOption?.name || '' });
        }

        const updatedOrder = {
            ...order,
            proteinOptions: updatedProteinOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_PROTEIN',
            recordId: order.recordId,
            timestamp: Date.now(),
            newProteinId,
            oldProteinId,
            upgradePrice,
            monetaryChange: upgradePrice > 0 ? true : false
        });

        flushSync(() => updateOrdersState(updatedOrders));

        const message = upgradePrice > 0
            ? `Protein upgraded! $${upgradePrice} will be charged when you save changes.`
            : 'Protein updated! Remember to "Save Changes" in Order Summary.';

        // setSuccessMessage(message);
        // setTimeout(() => setSuccessMessage(''), 5000);
        setShowPriceConfirm(null);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleVeggieSubstitution = (orderIndex, targetVeggieId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const targetVeggie = order.veggieOptions?.find(opt => opt.id === targetVeggieId);

        // Update local state - toggle veggie active status
        const updatedVeggieOptions = order.veggieOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === targetVeggieId ? !isCurrentlyActive : opt.isActive
        }));

        // Update finalIngredients (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (isCurrentlyActive) {
            // Remove veggie
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== targetVeggieId);
        } else {
            // Add veggie
            if (!updatedFinalIngredients.some(ing => ing.id === targetVeggieId)) {
                updatedFinalIngredients.push({ id: targetVeggieId, name: targetVeggie?.name || '' });
            }
        }

        const updatedOrder = {
            ...order,
            veggieOptions: updatedVeggieOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'TOGGLE_VEGGIE',
            recordId: order.recordId,
            veggieId: targetVeggieId,
            veggieName: targetVeggie?.name,
            action: isCurrentlyActive ? 'remove' : 'add',
            timestamp: Date.now(),
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage(`Veggie ${isCurrentlyActive ? 'removed' : 'added'}! Remember to "Save Changes" in Order Summary.`);
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleSauceSubstitution = (orderIndex, newSauceId, oldSauceId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const oldSauce = order.sauceOptions?.find(opt => opt.id === oldSauceId);
        const newSauce = order.sauceOptions?.find(opt => opt.id === newSauceId);

        // Update local state - update sauce options to reflect new selection
        const updatedSauceOptions = order.sauceOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === newSauceId
        }));

        // Update finalIngredients - replace old sauce with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        // Remove all sauce options first
        const allSauceIds = order.sauceOptions?.map(opt => opt.id) || [];
        updatedFinalIngredients = updatedFinalIngredients.filter(ing => !allSauceIds.includes(ing.id));
        // Add new sauce if selected
        if (newSauceId) {
            updatedFinalIngredients.push({ id: newSauceId, name: newSauce?.name || '' });
        }

        const updatedOrder = {
            ...order,
            sauceOptions: updatedSauceOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_SAUCE',
            recordId: order.recordId,
            newSauceId,
            oldSauceId,
            timestamp: Date.now(),
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage('Sauce updated! Remember to "Save Changes" in Order Summary.');
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleStarchSubstitution = (orderIndex, newStarchId, oldStarchId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const oldStarch = order.starchOptions?.find(opt => opt.id === oldStarchId);
        const newStarch = order.starchOptions?.find(opt => opt.id === newStarchId);

        // Update local state - update starch options to reflect new selection
        const updatedStarchOptions = order.starchOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === newStarchId
        }));

        // Update finalIngredients - replace old starch with new (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        // Remove all starch options first
        const allStarchIds = order.starchOptions?.map(opt => opt.id) || [];
        updatedFinalIngredients = updatedFinalIngredients.filter(ing => !allStarchIds.includes(ing.id));
        // Add new starch if selected
        if (newStarchId) {
            updatedFinalIngredients.push({ id: newStarchId, name: newStarch?.name || '' });
        }

        const updatedOrder = {
            ...order,
            starchOptions: updatedStarchOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'REPLACE_STARCH',
            recordId: order.recordId,
            timestamp: Date.now(),
            newStarchId,
            oldStarchId,
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage('Starch updated! Remember to "Save Changes" in Order Summary.');
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleToggleGarnish = (orderIndex, garnishId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = orders[orderIndex];
        const targetGarnish = order.garnishOptions?.find(opt => opt.id === garnishId);

        // Update local state - toggle garnish active status
        const updatedGarnishOptions = order.garnishOptions?.map(opt => ({
            ...opt,
            isActive: opt.id === garnishId ? !isCurrentlyActive : opt.isActive
        }));

        // Update finalIngredients (maintain {id, name} structure)
        let updatedFinalIngredients = [...(order.finalIngredients || [])];
        if (isCurrentlyActive) {
            // Remove garnish
            updatedFinalIngredients = updatedFinalIngredients.filter(ing => ing.id !== garnishId);
        } else {
            // Add garnish
            if (!updatedFinalIngredients.some(ing => ing.id === garnishId)) {
                updatedFinalIngredients.push({ id: garnishId, name: targetGarnish?.name || '' });
            }
        }

        const updatedOrder = {
            ...order,
            garnishOptions: updatedGarnishOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;

        // Track the change
        trackPendingChange(selectedDeliveryDate, {
            type: 'TOGGLE_GARNISH',
            recordId: order.recordId,
            garnishId,
            action: isCurrentlyActive ? 'remove' : 'add',
            monetaryChange: false
        });

        flushSync(() => updateOrdersState(updatedOrders));
        // setSuccessMessage(`Garnish ${isCurrentlyActive ? 'removed' : 'added'}! Remember to "Save Changes" in Order Summary.`);
        // setTimeout(() => setSuccessMessage(''), 5000);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    // Render loading state
    if (loading) {
        return (
            <CountdownLoader
                duration={30}
                onComplete={() => setLoading(false)}
            />
        );
    }

    // Render error state
    if (error) {
        return (
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
                <div style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '8px',
                    padding: '20px'
                }}>
                    <h2 style={{ color: '#c33', marginBottom: '10px' }}>Error</h2>
                    <p style={{ color: '#c33' }}>{error}</p>
                    <button
                        onClick={fetchMealPlan}
                        style={{
                            marginTop: '15px',
                            padding: '10px 20px',
                            backgroundColor: '#c33',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Render no data state only if truly no data (no meal plan AND no delivery dates)
    if (!customerDetails || (!deliveryDates.length && !orders.length)) {
        return (
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
                <h2>No Meal Plan Found</h2>
                <p>No meal plan data found for this customer.</p>
            </div>
        );
    }

    const customer = customerDetails.customer;
    const subscriptions = customerDetails.subscriptions;

    return (
        <div className="meal-plan-layout" style={{ backgroundColor: 'rgba(255,252,248,255)' }}>
            <div className="meal-plan-content">

                <MealPlanHeader customer={customer} />
                <SuccessMessage
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />
                <ErrorMessage
                    message={error}
                    onClose={() => setError(null)}
                />

                <DeliveryDateTabs
                    customerDetails={customerDetails}
                    deliveryDates={deliveryDates}
                    selectedDeliveryDate={selectedDeliveryDate}
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryStatusState={deliveryStatusState}
                    onDeliveryDateChange={handleDeliveryDateChange}
                    customerToken={token}
                    isLocked={isLocked}
                    subscriptionSummary={subscriptions.find(sub => sub.nextDeliveryDate === selectedDeliveryDate)}
                    onSkipConfirmed={(selectedNextDeliveryDate) => {
                        // Process orders - one-time operation when skip is confirmed
                        const currentOrders = ordersByDeliveryDate[selectedDeliveryDate] || [];
                        const processedOrders = currentOrders
                            .filter(order => order.isCore) // Keep only core meals
                            .map(order => ({
                                ...order,
                                isOrdered: false,  // Mark as not ordered
                                quantity: 0        // Reset quantity
                            }));

                        // Update orders for this delivery date
                        setOrdersByDeliveryDate(prev => ({
                            ...prev,
                            [selectedDeliveryDate]: processedOrders
                        }));

                        // Update current view
                        setOrders(processedOrders);
                        flushSync(() => {
                            setDeliveryStatusState(prev => {
                                const newState = {
                                    ...prev,
                                    [selectedDeliveryDate]: {
                                        ...prev[selectedDeliveryDate],
                                        isSkipped: true,
                                        nextDeliveryDate: selectedNextDeliveryDate
                                    }
                                };
                                return newState;
                            });
                        });

                        // Clear any pending changes for this skipped date
                        clearPendingChanges(selectedDeliveryDate);

                        setSuccessMessage(`Delivery skipped! Next delivery date is ${selectedNextDeliveryDate}`);
                        setTimeout(() => setSuccessMessage(''), 10000);
                    }}
                />

                {/* Resume Delivery Banner - only show if delivery is skipped */}
                {deliveryStatusState[selectedDeliveryDate]?.isSkipped && !isLocked && (
                    <ResumeDelivery
                        selectedDeliveryDate={selectedDeliveryDate}
                        customerToken={token}
                        nextDeliveryDate={deliveryStatusState[selectedDeliveryDate]?.nextDeliveryDate}
                        onResumeConfirmed={() => {
                            // Immediately update this date's status to not skipped
                            flushSync(() => {
                                setDeliveryStatusState(prev => {
                                    const newState = {
                                        ...prev,
                                        [selectedDeliveryDate]: {
                                            ...prev[selectedDeliveryDate],
                                            isSkipped: false,
                                            nextDeliveryDate: null
                                        }
                                    };

                                    return newState;
                                });
                            });
                            setSuccessMessage(`${selectedDeliveryDate} delivery resumed! You can now add meals to your order.`);
                            setTimeout(() => setSuccessMessage(''), 8000);
                        }}
                    />
                )}

                <LockMessage isLocked={isLocked} />
                {/* Meal Orders */}
                <div ref={containerRef} style={{ padding: '0px 20px 20px 20px', position: 'relative' }}>

                    {/* Group orders by meal type */}
                    {MEAL_TYPES.map(mealType => {
                        const mealOrders = orders.filter(order => order.meal === mealType);
                        const scrollState = getScrollState(mealType);

                        if (mealOrders.length === 0) return null;

                        const subscriptionCount = getSubscriptionCount(mealType);
                        const orderedCount = getOrderedMealCount(mealType, selectedDeliveryDate);


                        return (
                            <div key={mealType} style={{ marginBottom: '30px' }}>
                                <h4 style={{
                                    color: '#333',
                                    marginBottom: '15px',
                                    paddingBottom: '5px',
                                    borderBottom: '2px solid #eee'
                                }}>
                                    {mealType}
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        fontWeight: 'normal',
                                        marginLeft: '8px'
                                    }}>
                                        ({getSubscriptionText(mealType, subscriptionCount, orderedCount)})
                                    </span>
                                </h4>

                                <div style={{ position: 'relative' }}>
                                    {/* Scrolling container */}
                                    <div
                                        data-meal-type={mealType}
                                        onScroll={(e) => handleScroll(mealType, e)}
                                        style={{
                                            display: 'flex',
                                            overflowX: 'auto',
                                            gap: '15px',
                                            paddingBottom: '10px'
                                        }}
                                    >
                                        {mealOrders.map((order) => {
                                            const actualIndex = orders.indexOf(order);
                                            return (
                                                <CompactMealCard
                                                    key={order.recordId || actualIndex}
                                                    meal={order}
                                                    actualIndex={actualIndex}
                                                    onOpenModal={openModal}
                                                    onQuantityChange={handleQuantityChange}
                                                    isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                                                    saving={saving}
                                                />
                                            );
                                        })}
                                    </div>

                                    <ScrollArrows
                                        mealType={mealType}
                                        shouldShowArrows={shouldShowArrows}
                                        itemCount={mealOrders.length}
                                        scrollState={scrollState}
                                        onScrollLeft={scrollLeft}
                                        onScrollRight={scrollRight}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Modal Components */}
                {showModal && selectedMeal && (
                    <MealModal
                        meal={selectedMeal.actualIndex !== undefined ?
                            { ...orders[selectedMeal.actualIndex], actualIndex: selectedMeal.actualIndex } :
                            selectedMeal
                        }
                        onClose={closeModal}
                        modalRef={modalRef}
                        saving={saving}
                        // Pass all the substitution handlers as props to the modal
                        onProteinSubstitution={handleProteinSubstitution}
                        onVeggieSubstitution={handleVeggieSubstitution}
                        onSauceSubstitution={handleSauceSubstitution}
                        onStarchSubstitution={handleStarchSubstitution}
                        onToggleGarnish={handleToggleGarnish}
                        isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                        showAddedBanner={showAddedBanner}
                        subscriptions={subscriptions}
                    />
                )}

                {showPriceConfirm && (
                    <PremiumProteinUpgradeModal
                        confirmData={showPriceConfirm}
                        onConfirm={() => {
                            executeProteinSubstitution(
                                showPriceConfirm.orderIndex,
                                showPriceConfirm.newProteinId,
                                showPriceConfirm.oldProteinId,
                                showPriceConfirm.price
                            );
                        }}
                        onCancel={() => setShowPriceConfirm(null)}
                    />
                )}

                {/* Summary Modal - only for tablet & mobile */}
                {showSummaryModal && !isDesktop && (
                    <SummaryModal
                        ordersByDeliveryDate={ordersByDeliveryDate}
                        deliveryDates={deliveryDates}
                        billingChanges={billingChanges}
                        customerToken={token}
                        pendingChanges={pendingChanges}
                        onSaveSuccess={(date, results) => {
                            updateOrderRecordIds(date, results);
                            clearPendingChanges(date);
                            // Success message is shown by OrderSummaryContent for all dates
                        }}
                        onClose={() => setShowSummaryModal(false)}
                        customerDetails={customerDetails}
                    />
                )}
            </div>

            {/* Desktop: Fixed sidebar */}
            {isDesktop && (
                <OrderSummarySidebar
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryDates={deliveryDates}
                    billingChanges={billingChanges}
                    customerToken={token}
                    pendingChanges={pendingChanges}
                    onSaveSuccess={(date, results) => {
                        updateOrderRecordIds(date, results);
                        clearPendingChanges(date);
                        // Success message is shown by OrderSummaryContent for all dates
                    }}
                    customerDetails={customerDetails}
                />
            )}

            {/* Tablet & Mobile: Floating button */}
            {!isDesktop && (
                <FloatingOrderButton onClick={() => setShowSummaryModal(true)} />
            )}
        </div>
    );
}